import { CollectionViewer } from '@angular/cdk/collections';
import { AgrDataSource } from '@next/core-lib/table-v2';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { ReturnableEmptyProductsService, ReturnableEmptyProduct } from '../services/returnable-empty-products.service';

export interface EmptyProduct {
  id: number;
  externalId: string;
  title: string;
  unit: string;
  productType: string;
  isReturnAllowed: boolean;
  maxReturnableQuantity?: number;
  orderUnitQuantity?: number;
  skuDetails: {
    title: string;
    externalId: string;
    unit: string;
    id?: number;
  };
}

export class EmptyProductsDataSource extends AgrDataSource<EmptyProduct> {
  private readonly dataSubject = new BehaviorSubject<EmptyProduct[]>([]);
  private readonly searchSubject = new BehaviorSubject<string>('');

  constructor(private readonly returnableEmptyProductsService: ReturnableEmptyProductsService) {
    super();

    // Set up reactive search with debouncing
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => this.loadingSubject.next(true)),
        switchMap((searchTerm) =>
          this.returnableEmptyProductsService.getReturnableEmptyProducts(searchTerm).pipe(
            map((products) => this.mapToEmptyProducts(products)),
            catchError((error) => {
              console.error('Error loading returnable empty products:', error);
              return of([]);
            }),
          ),
        ),
        tap(() => this.loadingSubject.next(false)),
      )
      .subscribe((products) => {
        this.dataSubject.next(products);
      });

    // Initial load
    this.loadData();
  }

  connect(_collectionViewer?: CollectionViewer): Observable<EmptyProduct[]> {
    return this.dataSubject.asObservable();
  }

  applyFilter(searchTerm: string) {
    this.searchSubject.next(searchTerm);
  }

  private loadData() {
    this.loadingSubject.next(true);
    this.returnableEmptyProductsService
      .getReturnableEmptyProducts()
      .pipe(
        map((products) => this.mapToEmptyProducts(products)),
        catchError((error) => {
          console.error('Error loading returnable empty products:', error);
          return of([]);
        }),
      )
      .subscribe((products) => {
        this.dataSubject.next(products);
        this.loadingSubject.next(false);
      });
  }

  private mapToEmptyProducts(products: ReturnableEmptyProduct[]): EmptyProduct[] {
    return products.map((product) => ({
      id: product.id,
      externalId: product.externalId,
      title: product.title,
      unit: product.unit,
      productType: product.productType,
      isReturnAllowed: product.isReturnAllowed,
      maxReturnableQuantity: product.maxReturnableQuantity,
      orderUnitQuantity: product.orderUnitQuantity,
      skuDetails: {
        id: product.id,
        title: product.title,
        externalId: product.externalId,
        unit: product.unit,
      },
    }));
  }

  disconnect(_collectionViewer?: CollectionViewer): void {
    if (!this.dataSubject.closed) {
      this.dataSubject.complete();
    }
    if (!this.searchSubject.closed) {
      this.searchSubject.complete();
    }
    if (!this.loadingSubject.closed) {
      this.loadingSubject.complete();
    }
    super.disconnect(_collectionViewer);
  }
}
